/**
 * Rise choreography — 4-phase anticipation → flash → rise → settle sequence.
 */

import { VOID } from '../../constants';
import type { VoidState } from './types';
import { visibleX } from './types';
import { getEdgeY } from './edge';

export function updateRiseChoreography(dt: number, voidY: number, state: VoidState): void {
  if (!state.risePhaseActive) return;
  state.risePhaseTimer += dt;

  const anticipation = VOID.RISE_ANTICIPATION;
  const moveEnd = anticipation + VOID.RISE_MOVE_DURATION;
  const settleEnd = moveEnd + VOID.RISE_SETTLE;
  const t = state.risePhaseTimer;

  // Phase 1: Anticipation (0 → 0.4s)
  if (t < anticipation) {
    state.riseIntensity = (t / anticipation) * 0.7;
    // Building screen shake
    if (state.screenShakeRef && t > 0.15) {
      state.screenShakeRef.trigger(1 + (t / anticipation) * 3, 0.7);
    }
  }
  // Phase 2: Flash (at anticipation boundary)
  else if (!state.phase2Fired) {
    state.phase2Fired = true;
    state.riseIntensity = 1.0;
    state.edgeWaveAmp = VOID.EDGE_RISE_AMP_MULT;
    // Purple screen flash + shakes
    if (state.effectsRef) state.effectsRef.triggerFlash(VOID.RISE_FLASH_COLOR, VOID.RISE_FLASH_ALPHA);
    if (state.cameraRef) state.cameraRef.shake(VOID.RISE_SHAKE, { decay: VOID.RISE_SHAKE_DECAY });
    if (state.screenShakeRef) state.screenShakeRef.trigger(VOID.RISE_SCREEN_SHAKE, VOID.RISE_SCREEN_SHAKE_DECAY);
  }
  // Phase 3: Rise (0.4s → moveEnd) — particle eruption at movement start
  else if (t < moveEnd) {
    state.riseIntensity = 1.0;

    if (!state.phase3Fired && state.particlesRef) {
      state.phase3Fired = true;
      // Edge particles with dark contrast (Sakurai: mix dark elements)
      const rv = visibleX();
      const spacing = rv.w / 12;
      for (let i = 0; i < 12; i++) {
        const px = rv.off + spacing * i + spacing * 0.5;
        const py = getEdgeY(px, voidY, state);
        // Bright magenta
        state.particlesRef.spawnExplosion(px, py, VOID.EDGE_COLOR, 3);
        // Deep purple accent
        state.particlesRef.spawnExplosion(px, py, 0x8800ff, 2);
        // Dark contrast particles (Sakurai dark framing)
        state.particlesRef.spawnExplosion(px, py, 0x110022, 1);
        if (Math.random() < 0.5) {
          state.particlesRef.spawnExplosion(px, py, 0x050008, 1);
        }
      }
    }
  }
  // Phase 4: Settle (moveEnd → settleEnd)
  else if (t < settleEnd) {
    const settleT = (t - moveEnd) / VOID.RISE_SETTLE;
    state.riseIntensity = 1.0 - settleT;
    state.edgeWaveAmp = 1 + (VOID.EDGE_RISE_AMP_MULT - 1) * (1 - settleT);
  }
  // Done
  else {
    state.riseIntensity = 0;
    state.edgeWaveAmp = 1;
    state.risePhaseActive = false;
    state.particlesRef = null;
    state.effectsRef = null;
    state.cameraRef = null;
    state.screenShakeRef = null;
  }
}
